import { Callout } from "@octopusdeploy/design-system-components";
import { Permission } from "@octopusdeploy/octopus-server-client";
import type { CustomFieldResource, TenantResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { cloneDeep } from "lodash";
import * as React from "react";
import type { AnalyticActionDispatcher } from "~/analytics/Analytics";
import { Action } from "~/analytics/Analytics";
import { repository } from "~/clientInstance";
import type { DataBaseComponentState } from "~/components/DataBaseComponent";
import { DataBaseComponent } from "~/components/DataBaseComponent";
import InternalLink from "~/components/Navigation/InternalLink";
import { CustomFieldSelect } from "~/components/form/CustomeField/CustomFieldSelect";
import SaveDialogLayout from "../../../components/DialogLayout/SaveDialogLayout";
interface AddCustomFieldsToTenantDialogProps {
    tenant: TenantResource;
    customFields: CustomFieldResource[];
    onUpdated(tenant: TenantResource): void;
    dispatchAction: AnalyticActionDispatcher;
}
interface AddCustomFieldsToTenantDialogState extends DataBaseComponentState {
    customFieldIdToSelectedCustomFieldValueIds: Map<string, string[]>;
}
const parseSelectedCustomFieldValueIdsFromCanonicalCustomFieldValues = (customFields: CustomFieldResource[], canonicalCustomFieldValues: string[]): Map<string, string[]> => {
    const customFieldIdToSelectedCustomFieldValueIds = new Map<string, string[]>();
    customFields.forEach((customField) => {
        const fieldValueIds: string[] = [];
        customField.Values?.forEach((value) => {
            if (value.CanonicalCustomFieldValueName && canonicalCustomFieldValues.includes(value.CanonicalCustomFieldValueName)) {
                fieldValueIds.push(value.Id);
            }
        });
        customFieldIdToSelectedCustomFieldValueIds.set(customField.Id, fieldValueIds);
    });
    return customFieldIdToSelectedCustomFieldValueIds;
};
const getCanonicalCustomFieldValuesFromSelectedCustomFieldValueIds = (customFields: CustomFieldResource[], customFieldIdToCustomFieldValueIds: Map<string, string[]>): string[] => {
    const canonicalCustomFieldValues: string[] = [];
    customFields.forEach((customField) => {
        const fieldValueIds = customFieldIdToCustomFieldValueIds.get(customField.Id) || [];
        fieldValueIds.forEach((valueId) => {
            const value = customField.Values?.find((v) => v.Id === valueId);
            if (value?.CanonicalCustomFieldValueName) {
                canonicalCustomFieldValues.push(value.CanonicalCustomFieldValueName);
            }
        });
    });
    return canonicalCustomFieldValues;
};
export default class AddCustomFieldsToTenantDialog extends DataBaseComponent<AddCustomFieldsToTenantDialogProps, AddCustomFieldsToTenantDialogState> {
    constructor(props: AddCustomFieldsToTenantDialogProps) {
        super(props);
        const customFieldIdToSelectedCustomFieldValueIds = parseSelectedCustomFieldValueIdsFromCanonicalCustomFieldValues(props.customFields, props.tenant.CustomFields ?? []);
        this.state = {
            customFieldIdToSelectedCustomFieldValueIds,
        };
    }
    handleCustomFieldChange = (customFieldId: string, newValues: string[]) => {
        const customFieldIdToSelectedCustomFieldValueIds = new Map(this.state.customFieldIdToSelectedCustomFieldValueIds);
        customFieldIdToSelectedCustomFieldValueIds.set(customFieldId, newValues);
        this.setState({
            customFieldIdToSelectedCustomFieldValueIds,
        });
    };
    save = async () => {
        this.props.dispatchAction("Manage Tenant CustomFields", { resource: "Tenant", action: Action.Save });
        return this.doBusyTask(async () => {
            let tenant = cloneDeep(this.props.tenant);
            tenant.CustomFields = getCanonicalCustomFieldValuesFromSelectedCustomFieldValueIds(this.props.customFields, this.state.customFieldIdToSelectedCustomFieldValueIds);
            tenant = await repository.Tenants.save(tenant);
            setTimeout(() => this.props.onUpdated(tenant), 0);
            return true;
        });
    };
    render() {
        const customFieldEmpty = this.props.customFields.length > 0 && !this.props.customFields.some((f) => f.Values && f.Values.length > 0);
        return (<SaveDialogLayout title={"Manage Tenant Custom Fields"} busy={this.state.busy} errors={this.errors} onSaveClick={this.save} savePermission={{ permission: Permission.TenantEdit, tenant: this.props.tenant.Id }} saveButtonDisabled={this.props.customFields.length === 0 || customFieldEmpty}>
                {this.props.customFields && this.props.customFields.length === 0 && (<Callout type={"information"} title="No Tenant Custom Fields">
                        Create your first <InternalLink to={links.tenantTagSetsPage.generateUrl({ spaceId: this.props.tenant.SpaceId })}>Tenant Custom Field</InternalLink> now.
                    </Callout>)}
                {this.props.customFields && customFieldEmpty && (<Callout type={"information"} title="Custom Fields Are Empty">
                        Add custom fields to your <InternalLink to={links.tenantTagSetsPage.generateUrl({ spaceId: this.props.tenant.SpaceId })}>Tenant Custom Fields</InternalLink> now.
                    </Callout>)}
                {this.props.customFields.map((customField, index) => {
                const fieldValues = this.state.customFieldIdToSelectedCustomFieldValueIds.get(customField.Id) || [];
                return <CustomFieldSelect key={customField.Id} customField={customField} value={fieldValues} onChange={(newValues) => this.handleCustomFieldChange(customField.Id, newValues)} autoFocus={index === 0}/>;
            })}
            </SaveDialogLayout>);
    }
    static displayName = "AddCustomFieldsToTenantDialog";
}
